import {useEffect,useId,useRef,useState} from 'react';
import './create-folder-dialog.css';

export interface CreateFolderDialogProps {
  open:boolean;
  parentName:string;
  onCreate:(name:string)=>Promise<void>;
  onClose:()=>void;
}

/** Each opening starts a fresh form; only a successful server response closes it. */
export function CreateFolderDialog({open,...props}:CreateFolderDialogProps) {
  return open?<FolderForm {...props}/>:null;
}

function FolderForm({parentName,onCreate,onClose}:Omit<CreateFolderDialogProps,'open'>) {
  const [name,setName]=useState(''),[error,setError]=useState(''),[busy,setBusy]=useState(false);
  const dialog=useRef<HTMLDialogElement>(null),input=useRef<HTMLInputElement>(null);
  const mounted=useRef(false),submitting=useRef(false);
  const id=useId();
  useEffect(()=>{
    mounted.current=true;
    const previous=document.activeElement instanceof HTMLElement?document.activeElement:null;
    const element=dialog.current;
    element?.showModal();input.current?.focus();
    return()=>{
      mounted.current=false;
      element?.close();
      if(previous?.isConnected&&previous!==document.body)previous.focus();
    };
  },[]);

  async function submit(event:React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if(submitting.current)return;
    const value=name.trim().normalize('NFC');
    const issue=!value?'请输入文件夹名称。':value.length>100?'文件夹名称不能超过 100 个字符。':
      value==='.'||value==='..'?'请使用其他文件夹名称。':/[\\/\u0000-\u001f\u007f]/u.test(value)?'文件夹名称不能包含斜杠或控制字符。':'';
    if(issue){setError(issue);input.current?.focus();return;}
    submitting.current=true;setBusy(true);setError('');
    try {
      await onCreate(value);
      if(mounted.current)onClose();
    } catch(value) {
      if(mounted.current){setError(value instanceof Error?value.message:'创建失败，请重试。');input.current?.focus();}
    } finally {
      submitting.current=false;
      if(mounted.current)setBusy(false);
    }
  }

  return <dialog ref={dialog} className="library-create-folder-dialog" aria-labelledby={`${id}-title`} aria-describedby={`${id}-destination`} onCancel={event=>{event.preventDefault();if(!submitting.current)onClose();}}>
    <form onSubmit={event=>void submit(event)} noValidate aria-busy={busy}>
      <h3 id={`${id}-title`}>新建文件夹</h3>
      <p id={`${id}-destination`}>创建位置：<span title={parentName}>{parentName}</span></p>
      <label htmlFor={`${id}-name`}>文件夹名称</label>
      <input ref={input} id={`${id}-name`} name="folderName" type="text" autoComplete="off" maxLength={100} placeholder="输入文件夹名称" required readOnly={busy} value={name} aria-invalid={!!error} aria-describedby={error?`${id}-error`:undefined}
        onChange={event=>{setName(event.target.value);setError('');}}
        onKeyDown={event=>{if(event.key==='Enter'&&(event.nativeEvent.isComposing||event.keyCode===229))event.preventDefault();}}/>
      {error&&<p id={`${id}-error`} className="library-create-folder-error" role="alert">{error}</p>}
      <div className="library-create-folder-actions">
        <button type="button" disabled={busy} onClick={onClose}>取消</button>
        <button type="submit" disabled={busy||!name.trim()}>{busy?'正在创建…':'创建'}</button>
      </div>
    </form>
  </dialog>;
}
